import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { get } from '../utils/apiClient';
import { logger } from "../utils/logger";
import { toast } from 'react-toastify';
import {
  initialActiveChart,
  surfaceHasAggregate,
  type ChartScope,
} from '../features/client-portal/utils/chartNavigation';

const ActiveChartContext = createContext(null);

export function useActiveChart() {
  const context = useContext(ActiveChartContext);
  if (!context) {
    return {
      activePatientId: null,
      activeCaseId: null,
      activePatientName: null,
      linkedCharts: [],
      selfPatientId: null,
      roleInCase: 'self',
      switchChart: () => {},
      clearActiveChart: () => {},
      enterSurface: () => {},
      surfaceScope: 'none',
      surfaceEligibleIds: [],
      hasAggregateForSurface: false,
      isAggregate: true,
      hasMultipleCharts: false,
      isViewingOwnChart: true,
      loading: true,
      viewerName: '',
      activeChartContext: null,
    };
  }
  return context;
}

export function ActiveChartProvider({ children, user }) {
  // activePatientId === null means the AGGREGATE / household view (no chart
  // drilled in). ActiveChartContext is the SINGLE SOURCE OF TRUTH for which
  // chart (if any) is active; PatientContext delegates its selection here.
  const [activePatientId, setActivePatientId] = useState(null);
  const [activeCaseId, setActiveCaseId] = useState(null);
  const [activePatientName, setActivePatientName] = useState(null);
  const [roleInCase, setRoleInCase] = useState('self');
  const [linkedCharts, setLinkedCharts] = useState([]);
  const [selfPatientId, setSelfPatientId] = useState(null);
  const [selfContext, setSelfContext] = useState(null);
  const [viewerName, setViewerName] = useState('');
  const [loading, setLoading] = useState(true);

  // The current surface's chart scope + the ids eligible for it. Registered by
  // the surface (ClientPortalPage per-tab) via enterSurface so the persistent
  // chip and switchChart guard stay in lockstep with what the tab offers.
  const [surfaceScope, setSurfaceScope] = useState<ChartScope>('none');
  const [surfaceEligibleIds, setSurfaceEligibleIds] = useState([]);

  const isClientUser = user?.role === 'client' || user?.character_type === 'client'
    || user?.character_type === 'parent_of_client' || user?.character_type === 'part_of_couple';

  // Live refs so the memoized enterSurface/switchChart callbacks read current
  // eligibility/name data without changing identity on every render.
  const eligibleIdsRef = useRef(surfaceEligibleIds);
  eligibleIdsRef.current = surfaceEligibleIds;
  // The chart the viewer last drilled into (PORTAL-08). Survives tab changes so
  // a guardian on their child's chart stays on it; cleared only by an explicit
  // return to the household view.
  const lastSelectionRef = useRef<{ patientId: string; caseId: string | null } | null>(null);

  // Deterministic resolution when several linked-chart contexts cover the same
  // patient and the caller did not name a Case (e.g. the self-chart picker card
  // always passes case_id=null). The patient's Case-less context — their own /
  // relationship-linked chart — is the intended target; a unique self context
  // is next. Mirrors the case_id==null preference PatientContext.selectedPatient
  // already applies. Anything else stays ambiguous and returns null.
  const resolveUnambiguousChart = useCallback((patientId) => {
    const matches = linkedCharts.filter((c) => String(c.patient_id) === String(patientId));
    if (matches.length === 1) return matches[0];
    const caseLess = matches.filter((c) => c.case_id == null);
    if (caseLess.length === 1) return caseLess[0];
    const selfCharts = matches.filter((c) => c.is_self === true);
    if (selfCharts.length === 1) return selfCharts[0];
    return null;
  }, [linkedCharts]);

  const findLinkedChart = useCallback((patientId, caseId = null) => {
    if (caseId != null) {
      return linkedCharts.find(
        (c) => String(c.patient_id) === String(patientId) && String(c.case_id) === String(caseId),
      ) || null;
    }
    return resolveUnambiguousChart(patientId);
  }, [linkedCharts, resolveUnambiguousChart]);

  const resolveName = useCallback((patientId, caseId = null) => {
    if (patientId == null) return null;
    const linked = findLinkedChart(patientId, caseId);
    if (linked?.name) return linked.name;
    if (caseId == null && String(patientId) === String(selfPatientId)) {
      const u = user || {};
      return [u.first_name, u.last_name].filter(Boolean).join(' ')
        || selfContext?.name
        || null;
    }
    return null;
  }, [findLinkedChart, selfPatientId, user, selfContext]);

  const resolveCaseId = useCallback((patientId) => {
    const matches = linkedCharts.filter((c) => String(c.patient_id) === String(patientId));
    const caseIds = [...new Set(matches.map((c) => c.case_id).filter(Boolean))];
    return caseIds.length === 1 ? caseIds[0] : null;
  }, [linkedCharts]);

  const loadChartData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await get('/api/client-portal/profile');
      if (!res.ok) return;

      const data = await res.json();
      const patientId = data.patient_id || null;
      const patientName = [data.first_name, data.last_name].filter(Boolean).join(' ') || null;
      const cases = data.linked_cases || [];

      // A (re)loaded household is a new authority snapshot: never carry a
      // remembered chart across it (PORTAL-08 review).
      lastSelectionRef.current = null;
      setSelfPatientId(patientId);
      setRoleInCase(data.role_in_case || 'self');
      setLinkedCharts(cases);
      setViewerName(patientName || '');
      setSelfContext({
        patientId,
        name: patientName || '',
        roleLabel: data.role_label || 'Primary patient',
        shareScope: data.share_scope || 'All info',
        relativeLabel: data.relative_label || 'self',
        isSelf: data.is_self !== false,
      });

      // Aggregate-first: multi-chart users open with NO active chart (household
      // summary); single-chart users land directly on their own chart. Per-tab
      // enterSurface refines this once a surface registers.
      if (cases.length > 0) {
        setActivePatientId(null);
        setActivePatientName(null);
        setActiveCaseId(null);
      } else {
        setActivePatientId(patientId);
        setActivePatientName(patientName);
        setActiveCaseId(null);
      }
    } catch (err) {
      logger.error('Failed to load chart data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user || !isClientUser) {
      setLoading(false);
      return;
    }
    loadChartData();
  }, [user, isClientUser, loadChartData]);

  // Drill into a concrete chart. Guarded: never activates a chart the current
  // surface's eligibility predicate rejects (Requirement 3 — e.g. a partner's
  // chart on the health surface must never become a drill target).
  const switchChart = useCallback((patientId, caseId = null) => {
    if (patientId == null) {
      lastSelectionRef.current = null;
      setActivePatientId(null);
      setActivePatientName(null);
      setActiveCaseId(null);
      return;
    }
    const eligible = eligibleIdsRef.current;
    if (eligible.length > 0 && !eligible.some((id) => String(id) === String(patientId))) {
      logger.warn('switchChart blocked: chart not eligible for current surface', patientId);
      toast.error('That chart is not available here.');
      return;
    }
    // Ambiguity only exists when the patient is reachable through more than one
    // linked chart context (e.g. a Case membership plus a relationship link).
    // A caller passing case_id=null explicitly wants the non-Case context, so
    // disambiguate deterministically (own/Case-less context first) BEFORE the
    // resolveCaseId fallback guesses a Case — a genuinely unresolvable pick is
    // refused, and never silently.
    const matchingCharts = linkedCharts.filter((c) => String(c.patient_id) === String(patientId));
    let selectedCaseId;
    if (caseId == null && matchingCharts.length > 1) {
      let resolvedChart = resolveUnambiguousChart(patientId);
      if (!resolvedChart && String(patientId) === String(selfPatientId)) {
        // The viewer's own chart is never truly ambiguous: "My chart"
        // (case_id=null) is the Case-less self view, which stays valid even
        // when linked_cases only carries Case-member contexts for self.
        resolvedChart = { patient_id: patientId, case_id: null, is_self: true };
      }
      if (!resolvedChart) {
        logger.warn('switchChart blocked: Case is required for an ambiguous chart', patientId);
        toast.error('Unable to open that chart — please pick a specific chart to view.');
        return;
      }
      selectedCaseId = resolvedChart.case_id || null;
    } else if (String(patientId) === String(selfPatientId)) {
      // When the viewer's own patient participates in multiple Cases, a pick
      // carrying a case_id that resolves in linkedCharts drills into that Case
      // context and must keep it. Otherwise normalize the pick to the
      // canonical Case-less self view — selfContext carries no Case, and
      // surfaces look the self row up without one.
      selectedCaseId = caseId != null && findLinkedChart(patientId, caseId) ? caseId : null;
    } else {
      selectedCaseId = caseId || resolveCaseId(patientId);
    }
    // The viewer's own chart is never in linkedCharts (linked_cases lists only
    // OTHER patients), so a self pick carrying her Case id must not be
    // membership-validated against that list — it always resolves to the self
    // context. Validating it fails closed and dead-ends the picker.
    if (
      caseId != null
      && String(patientId) !== String(selfPatientId)
      && !findLinkedChart(patientId, caseId)
    ) {
      logger.warn('switchChart blocked: chart is not a member of the selected Case', {
        patientId,
        caseId,
      });
      toast.error('Unable to open that chart — please pick a specific chart to view.');
      return;
    }
    lastSelectionRef.current = { patientId: String(patientId), caseId: selectedCaseId || null };
    setActivePatientId(patientId);
    setActivePatientName(resolveName(patientId, selectedCaseId));
    setActiveCaseId(selectedCaseId);
  }, [findLinkedChart, linkedCharts, resolveCaseId, resolveName, resolveUnambiguousChart, selfPatientId]);

  // Return to the aggregate/household view (no active chart).
  const clearActiveChart = useCallback(() => {
    lastSelectionRef.current = null;
    setActivePatientId(null);
    setActivePatientName(null);
    setActiveCaseId(null);
  }, []);

  // Register the current surface and apply the aggregate-vs-direct decision.
  // The viewer's last drilled-in chart wins whenever this surface offers it
  // (PORTAL-08: tab navigation must not silently drop a guardian back to
  // "Household — all charts"). Otherwise: >=2 eligible -> aggregate (null);
  // exactly 1 -> that chart; none -> null. 'none' surfaces are not
  // chart-scoped, so entering one clears the ACTIVE chart (no leak into
  // Settings/Messages) but keeps the last selection for the next chart tab.
  const enterSurface = useCallback((scope, eligibleIds) => {
    const ids = (eligibleIds || []).map((id) => String(id));
    setSurfaceScope(scope);
    setSurfaceEligibleIds(ids);
    eligibleIdsRef.current = ids;
    const last = lastSelectionRef.current;
    if (scope !== 'none' && last && ids.includes(last.patientId)) {
      setActivePatientId(last.patientId);
      setActivePatientName(resolveName(last.patientId, last.caseId));
      setActiveCaseId(last.caseId);
      return;
    }
    const candidate = initialActiveChart(scope, ids);
    const candidateCases = candidate
      ? linkedCharts.filter((c) => String(c.patient_id) === String(candidate))
      : [];
    const initial = candidate && candidateCases.filter((c) => c.case_id).length <= 1
      ? candidate
      : null;
    setActivePatientId(initial);
    const initialCaseId = initial ? resolveCaseId(initial) : null;
    setActivePatientName(initial ? resolveName(initial, initialCaseId) : null);
    setActiveCaseId(initialCaseId);
  }, [linkedCharts, resolveCaseId, resolveName]);

  const isAggregate = activePatientId === null;
  const isViewingOwnChart = activePatientId != null && String(activePatientId) === String(selfPatientId);
  const hasMultipleCharts = linkedCharts.length > 0;
  const hasAggregateForSurface = surfaceHasAggregate(surfaceScope, surfaceEligibleIds);

  // ChartContext for the ACTIVE chart, viewer-relative — the single object the
  // shared chartContext formatter consumes (persistent chip, Regarding header).
  // Null in aggregate view so the chip renders the neutral household state.
  const activeChartContext = useMemo(() => {
    if (isAggregate) return null;
    const linked = findLinkedChart(activePatientId, activeCaseId);
    // The viewer's own chart has no linked_cases entry at any case_id — fall
    // back to the self context whenever self is active.
    if (!linked && isViewingOwnChart) return selfContext;
    if (!linked) return null;
    return {
      patientId: linked.patient_id,
      caseId: linked.case_id || null,
      name: linked.name || 'Linked chart',
      roleLabel: linked.role_label || 'Linked',
      shareScope: linked.share_scope || 'All info',
      relativeLabel: linked.relative_label || 'linked',
      isSelf: linked.is_self === true,
    };
  }, [isAggregate, isViewingOwnChart, selfContext, findLinkedChart, activePatientId, activeCaseId]);

  const value = {
    activePatientId,
    activeCaseId,
    activePatientName,
    linkedCharts,
    selfPatientId,
    roleInCase,
    switchChart,
    clearActiveChart,
    enterSurface,
    surfaceScope,
    surfaceEligibleIds,
    hasAggregateForSurface,
    isAggregate,
    hasMultipleCharts,
    isViewingOwnChart,
    loading,
    viewerName,
    activeChartContext: activeChartContext
      ? { ...activeChartContext, viewerName }
      : null,
  };

  return (
    <ActiveChartContext.Provider value={value}>
      {children}
    </ActiveChartContext.Provider>
  );
}

export default ActiveChartContext;
